import { Form, Heading, OptionType, Text } from '@medely/ui-kit';
import React from 'react';
import ClinicalExperienceAndSpecialties from './components/ClinicalExperienceAndSpecialties';
import { PositionsSelect } from './components/PositionsSelect';
import useAllPositionsInMarkets from '../../../../hooks/useAllPositionsInMarkets';
import { FixedFooterLayout } from '../../../../components/FixedFooterLayout';
import { RecommendedPositions } from './components/RecommendedPositions';
import { SelectedPosition } from './types';
import { getRecommendedPositions } from '../../util';
import { useUserPositions } from './graphql';
import { positionsSchema } from './schema';
import { ContentLoader } from '@mp/components';
import usePositionInfoMutations from './mutations';
import { useApplicationMutations } from '../../applicationFlowMutations';
import { ApplicationButtons } from '../../components/ApplicationButtons';

const FIRST_STEP = 1;
const LAST_STEP = 3;

export const Positions = () => {
  const { currentUserLoading, currentUser } = useUserPositions();

  const { positions, isLoading: allPositionsLoading } = useAllPositionsInMarkets();
  const { submitPositionInfo } = usePositionInfoMutations();
  const { nextStep, prevStep, isLoading: mutationIsLoading } = useApplicationMutations();
  const [step, setStep] = React.useState(1);
  const [initialSubmittedPositions, setInitialSubmittedPositions] = React.useState<
    SelectedPosition[]
  >([]);
  const [recommendedPositions, setRecommendedPositions] = React.useState<OptionType[]>([]);
  const [triedToSubmit, setTriedToSubmit] = React.useState(false);
  const [hasNoPositionSelected, setHasNoPositionSelected] = React.useState(false);

  const initialSelectedPositions = currentUser?.professional?.professional_positions
    ?.map((professionalPosition) => {
      const fullPosition = positions.find((p) => p.id === professionalPosition?.position_id);
      const specialtyIds = !!fullPosition?.specialties
        ? fullPosition.specialties
            .map((s) => String(s?.id))
            .filter((id) =>
              currentUser?.professional?.specialties?.some((s) => String(s?.id) === id),
            )
        : [];
      return {
        id: professionalPosition?.position_id,
        years_of_experience: professionalPosition.years_of_experience,
        specialty_ids: specialtyIds,
      };
    })
    .sort((a, b) => {
      const itemOne = positions.find((p) => p.id === a.id);
      const itemTwo = positions.find((p) => p.id === b.id);
      return (itemOne?.display_name || '').localeCompare(itemTwo?.display_name || '', undefined, {
        sensitivity: 'base',
      });
    });

  const handleSubmit = async (values: {
    professional_positions: { positions: SelectedPosition[] };
  }) => {
    let mappedValues = values;
    if (values.professional_positions?.positions.length) {
      const orderedPositions = values.professional_positions?.positions.sort((a, b) => {
        const itemOne = positions.find((p) => p.id === a.id);
        const itemTwo = positions.find((p) => p.id === b.id);
        return (itemOne?.display_name || '').localeCompare(itemTwo?.display_name || '', undefined, {
          sensitivity: 'base',
        });
      });
      mappedValues = {
        professional_positions: {
          positions: orderedPositions,
        },
      };
    }

    await submitPositionInfo(mappedValues, {
      onSuccess: () => {
        const selectedPositions: SelectedPosition[] = [...values.professional_positions.positions];
        if (step === FIRST_STEP) {
          setInitialSubmittedPositions(selectedPositions);
        }
        handleContinue(selectedPositions);
      },
    });
  };

  const handleBackClick = () => {
    if (step === FIRST_STEP) {
      prevStep();
      return;
    }
    if (step === LAST_STEP && !recommendedPositions.length) {
      setStep(1);
      return;
    }
    setStep(step - 1);
  };

  const handleContinue = (initialPositions: SelectedPosition[]) => {
    if (step === LAST_STEP) {
      nextStep();
      return;
    }
    if (step === FIRST_STEP) {
      const newRecommendedPositions = getRecommendedPositions(positions, initialPositions);
      setRecommendedPositions(newRecommendedPositions);
      if (!newRecommendedPositions.length) {
        setStep(3);
        return;
      }
    }
    setStep(step + 1);
  };

  const baseValuesLoading = currentUserLoading || allPositionsLoading;
  const disableButtons = mutationIsLoading || baseValuesLoading;

  const showMissingPositionMessage = triedToSubmit && hasNoPositionSelected;

  const stepComponentMap: Record<string, React.ReactNode> = {
    1: <PositionsSelect allPositions={positions} onSelectAnyPosition={setHasNoPositionSelected} />,
    2: (
      <RecommendedPositions
        allPositions={positions}
        initialSubmittedPositions={initialSubmittedPositions}
      />
    ),
    3: <ClinicalExperienceAndSpecialties allPositions={positions} />,
  };

  const defaultValues = {
    professional_positions: {
      positions: initialSelectedPositions || [],
    },
  };

  return (
    <Form
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      schema={positionsSchema(step, LAST_STEP)}
    >
      <ContentLoader loading={baseValuesLoading}>
        <FixedFooterLayout
          title={
            step === FIRST_STEP && (
              <>
                <Heading size="s" color="text.primary" testId="positions-select-title">
                  Select positions with 8+ months of paid clinical experience
                </Heading>
                {showMissingPositionMessage && (
                  <Text size="m" color="state.error.secondary">
                    Select at least 1 position
                  </Text>
                )}
              </>
            )
          }
          footerContent={
            <ApplicationButtons
              disabled={disableButtons}
              backClick={handleBackClick}
              onContinueClick={() => setTriedToSubmit(true)}
              loading={disableButtons}
            />
          }
        >
          {stepComponentMap[String(step)]}
        </FixedFooterLayout>
      </ContentLoader>
    </Form>
  );
};
